/**
 * Enhancements for the profile-completion step.
 *
 * Seven jobs:
 *
 * 1. Capture — a QR code on an event poster points at any page with `?ref=CODE`.
 *    The code is stashed in local storage immediately, because the query string
 *    does not survive account registration (Shopify posts to /account and
 *    re-renders the register page without it when validation fails) — and
 *    because the customer may well finish signing up hours later.
 *
 * 2. Prefill — on the profile-completion step, drop the stored code, the
 *    signed-in customer's email and whatever the customer answered last time
 *    into the Shopify Forms embed. The embed renders an empty form on every
 *    visit, so without this a customer coming back to change one answer would
 *    retype the rest — and leaving them blank would wipe them on submit.
 *
 * 3. Validate — check what the customer typed against the referral codes the
 *    marketing team maintains as metaobjects. An unknown or expired code shows
 *    an inline notice and is dropped on submit, so registration is never
 *    blocked and no junk code reaches the customer record.
 *
 * 4. Country code — the phone field stores a plain metafield, so it has none of
 *    the country handling the native Shopify phone field brings. Put a dial
 *    code dropdown in front of it and stitch the two together on submit, so the
 *    customer record ends up with +852 61234567 rather than a bare local number
 *    nobody can dial from outside Hong Kong.
 *
 * 5. Success — once the form is submitted the embed shows its own thank-you
 *    message, so the heading above it is hidden and a link is offered back to
 *    the form.
 *
 * 6. Policy notice — the line the embed prints under the button is the app's
 *    own wording, names the store's policies without pointing at them, and
 *    cannot be edited in the app. It is replaced with the theme's own sentence
 *    and real links, so the copy lives where the rest of this step's copy
 *    lives.
 *
 * 7. Reopen — that link, and the Edit My Profile button on the account page,
 *    carry `?edit-profile=1`, the way back into a step that otherwise
 *    disappears for good once it has been completed. Clicked a
 *    second time it points at the page the customer is already on, so it
 *    reloads by hand rather than letting the browser do nothing. It also
 *    clears the mark Shopify Forms leaves in local storage on submission,
 *    without which the app renders nothing at all the next time round.
 *
 * The embed renders inside an open shadow root, so its fields are reachable but
 * only after the component has hydrated — hence the polling.
 */
(function () {
  var URL_PARAM = 'ref';
  var CODE_KEY = 'fx_referral_code';
  var FROM_LINK_KEY = 'fx_referral_from_link';
  var MAX_CODE_LENGTH = 64;
  var POLL_INTERVAL = 100;
  var POLL_LIMIT = 100; // ~10s
  var NOTICE_ATTR = 'data-referral-notice';
  var REGISTERED_KEY = 'fx_just_registered';
  var HIGHLIGHT_KEY = 'fx_highlight_profile';
  var PHONE_CLASS = 'fx-phone';
  var PHONE_STYLE_ATTR = 'data-fx-phone-style';
  var CODE_SEPARATOR = ' ';
  var SAVED_AT_KEY = 'fx_referral_saved_at';
  var CODE_TTL_DAYS = 30;
  var EDIT_PARAM = 'edit-profile';
  var REMINDER_ATTR = 'data-referral-reminder';
  var DISMISSED_SUFFIX = '-dismissed-after-submit';
  var RETRY_KEY = 'fx_profile_retry';
  var RECOVER_AFTER = 2500;
  var POLICY_ATTR = 'data-fx-policy';
  var PINNED_COUNTRIES = 5; // Hong Kong, Macau, mainland China, Taiwan, Singapore

  // CLDR's long name for these two is "中國香港特別行政區" / "Hong Kong SAR China":
  // accurate, and far too long for a dropdown. Everywhere else the long name is
  // the one people recognise.
  var SHORT_NAME = { HK: true, MO: true };

  var locale = 'en';
  var namers;

  // A full international dial code list, so a customer outside the handful of
  // markets marketing thought to list is not stuck retyping their number. Same
  // format as the theme setting — label first, dial code last — so both go
  // through one parser. The markets the store actually sells to sit on top.
  var DEFAULT_COUNTRY_CODES = [
    '🇭🇰 Hong Kong +852',
    '🇲🇴 Macau +853',
    '🇨🇳 China +86',
    '🇹🇼 Taiwan +886',
    '🇸🇬 Singapore +65',
    '🇦🇫 Afghanistan +93',
    '🇦🇱 Albania +355',
    '🇩🇿 Algeria +213',
    '🇦🇸 American Samoa +1684',
    '🇦🇩 Andorra +376',
    '🇦🇴 Angola +244',
    '🇦🇮 Anguilla +1264',
    '🇦🇬 Antigua and Barbuda +1268',
    '🇦🇷 Argentina +54',
    '🇦🇲 Armenia +374',
    '🇦🇼 Aruba +297',
    '🇦🇺 Australia +61',
    '🇦🇹 Austria +43',
    '🇦🇿 Azerbaijan +994',
    '🇧🇸 Bahamas +1242',
    '🇧🇭 Bahrain +973',
    '🇧🇩 Bangladesh +880',
    '🇧🇧 Barbados +1246',
    '🇧🇾 Belarus +375',
    '🇧🇪 Belgium +32',
    '🇧🇿 Belize +501',
    '🇧🇯 Benin +229',
    '🇧🇲 Bermuda +1441',
    '🇧🇹 Bhutan +975',
    '🇧🇴 Bolivia +591',
    '🇧🇦 Bosnia and Herzegovina +387',
    '🇧🇼 Botswana +267',
    '🇧🇻 Bouvet Island +47',
    '🇧🇷 Brazil +55',
    '🇮🇴 British Indian Ocean Territory +246',
    '🇻🇬 British Virgin Islands +1284',
    '🇧🇳 Brunei +673',
    '🇧🇬 Bulgaria +359',
    '🇧🇫 Burkina Faso +226',
    '🇧🇮 Burundi +257',
    '🇰🇭 Cambodia +855',
    '🇨🇲 Cameroon +237',
    '🇨🇦 Canada +1',
    '🇨🇻 Cape Verde +238',
    '🇧🇶 Caribbean Netherlands +599',
    '🇰🇾 Cayman Islands +1345',
    '🇨🇫 Central African Republic +236',
    '🇹🇩 Chad +235',
    '🇨🇱 Chile +56',
    '🇨🇽 Christmas Island +61',
    '🇨🇨 Cocos (Keeling) Islands +61',
    '🇨🇴 Colombia +57',
    '🇰🇲 Comoros +269',
    '🇨🇬 Congo +242',
    '🇨🇰 Cook Islands +682',
    '🇨🇷 Costa Rica +506',
    '🇭🇷 Croatia +385',
    '🇨🇺 Cuba +53',
    '🇨🇼 Curaçao +599',
    '🇨🇾 Cyprus +357',
    '🇨🇿 Czechia +420',
    '🇨🇩 DR Congo +243',
    '🇩🇰 Denmark +45',
    '🇩🇯 Djibouti +253',
    '🇩🇲 Dominica +1767',
    '🇩🇴 Dominican Republic +1',
    '🇪🇨 Ecuador +593',
    '🇪🇬 Egypt +20',
    '🇸🇻 El Salvador +503',
    '🇬🇶 Equatorial Guinea +240',
    '🇪🇷 Eritrea +291',
    '🇪🇪 Estonia +372',
    '🇸🇿 Eswatini +268',
    '🇪🇹 Ethiopia +251',
    '🇫🇰 Falkland Islands +500',
    '🇫🇴 Faroe Islands +298',
    '🇫🇯 Fiji +679',
    '🇫🇮 Finland +358',
    '🇫🇷 France +33',
    '🇬🇫 French Guiana +594',
    '🇵🇫 French Polynesia +689',
    '🇹🇫 French Southern and Antarctic Lands +262',
    '🇬🇦 Gabon +241',
    '🇬🇲 Gambia +220',
    '🇬🇪 Georgia +995',
    '🇩🇪 Germany +49',
    '🇬🇭 Ghana +233',
    '🇬🇮 Gibraltar +350',
    '🇬🇷 Greece +30',
    '🇬🇱 Greenland +299',
    '🇬🇩 Grenada +1473',
    '🇬🇵 Guadeloupe +590',
    '🇬🇺 Guam +1671',
    '🇬🇹 Guatemala +502',
    '🇬🇬 Guernsey +44',
    '🇬🇳 Guinea +224',
    '🇬🇼 Guinea-Bissau +245',
    '🇬🇾 Guyana +592',
    '🇭🇹 Haiti +509',
    '🇭🇳 Honduras +504',
    '🇭🇺 Hungary +36',
    '🇮🇸 Iceland +354',
    '🇮🇳 India +91',
    '🇮🇩 Indonesia +62',
    '🇮🇷 Iran +98',
    '🇮🇶 Iraq +964',
    '🇮🇪 Ireland +353',
    '🇮🇲 Isle of Man +44',
    '🇮🇱 Israel +972',
    '🇮🇹 Italy +39',
    '🇨🇮 Ivory Coast +225',
    '🇯🇲 Jamaica +1876',
    '🇯🇵 Japan +81',
    '🇯🇪 Jersey +44',
    '🇯🇴 Jordan +962',
    '🇰🇿 Kazakhstan +7',
    '🇰🇪 Kenya +254',
    '🇰🇮 Kiribati +686',
    '🇽🇰 Kosovo +383',
    '🇰🇼 Kuwait +965',
    '🇰🇬 Kyrgyzstan +996',
    '🇱🇦 Laos +856',
    '🇱🇻 Latvia +371',
    '🇱🇧 Lebanon +961',
    '🇱🇸 Lesotho +266',
    '🇱🇷 Liberia +231',
    '🇱🇾 Libya +218',
    '🇱🇮 Liechtenstein +423',
    '🇱🇹 Lithuania +370',
    '🇱🇺 Luxembourg +352',
    '🇲🇬 Madagascar +261',
    '🇲🇼 Malawi +265',
    '🇲🇾 Malaysia +60',
    '🇲🇻 Maldives +960',
    '🇲🇱 Mali +223',
    '🇲🇹 Malta +356',
    '🇲🇭 Marshall Islands +692',
    '🇲🇶 Martinique +596',
    '🇲🇷 Mauritania +222',
    '🇲🇺 Mauritius +230',
    '🇾🇹 Mayotte +262',
    '🇲🇽 Mexico +52',
    '🇫🇲 Micronesia +691',
    '🇲🇩 Moldova +373',
    '🇲🇨 Monaco +377',
    '🇲🇳 Mongolia +976',
    '🇲🇪 Montenegro +382',
    '🇲🇸 Montserrat +1664',
    '🇲🇦 Morocco +212',
    '🇲🇿 Mozambique +258',
    '🇲🇲 Myanmar +95',
    '🇳🇦 Namibia +264',
    '🇳🇷 Nauru +674',
    '🇳🇵 Nepal +977',
    '🇳🇱 Netherlands +31',
    '🇳🇨 New Caledonia +687',
    '🇳🇿 New Zealand +64',
    '🇳🇮 Nicaragua +505',
    '🇳🇪 Niger +227',
    '🇳🇬 Nigeria +234',
    '🇳🇺 Niue +683',
    '🇳🇫 Norfolk Island +672',
    '🇰🇵 North Korea +850',
    '🇲🇰 North Macedonia +389',
    '🇲🇵 Northern Mariana Islands +1670',
    '🇳🇴 Norway +47',
    '🇴🇲 Oman +968',
    '🇵🇰 Pakistan +92',
    '🇵🇼 Palau +680',
    '🇵🇸 Palestine +970',
    '🇵🇦 Panama +507',
    '🇵🇬 Papua New Guinea +675',
    '🇵🇾 Paraguay +595',
    '🇵🇪 Peru +51',
    '🇵🇭 Philippines +63',
    '🇵🇳 Pitcairn Islands +64',
    '🇵🇱 Poland +48',
    '🇵🇹 Portugal +351',
    '🇵🇷 Puerto Rico +1',
    '🇶🇦 Qatar +974',
    '🇷🇴 Romania +40',
    '🇷🇺 Russia +7',
    '🇷🇼 Rwanda +250',
    '🇷🇪 Réunion +262',
    '🇧🇱 Saint Barthélemy +590',
    '🇸🇭 Saint Helena +290',
    '🇰🇳 Saint Kitts and Nevis +1869',
    '🇱🇨 Saint Lucia +1758',
    '🇲🇫 Saint Martin +590',
    '🇵🇲 Saint Pierre and Miquelon +508',
    '🇻🇨 Saint Vincent and the Grenadines +1784',
    '🇼🇸 Samoa +685',
    '🇸🇲 San Marino +378',
    '🇸🇦 Saudi Arabia +966',
    '🇸🇳 Senegal +221',
    '🇷🇸 Serbia +381',
    '🇸🇨 Seychelles +248',
    '🇸🇱 Sierra Leone +232',
    '🇸🇽 Sint Maarten +1721',
    '🇸🇰 Slovakia +421',
    '🇸🇮 Slovenia +386',
    '🇸🇧 Solomon Islands +677',
    '🇸🇴 Somalia +252',
    '🇿🇦 South Africa +27',
    '🇬🇸 South Georgia +500',
    '🇰🇷 South Korea +82',
    '🇸🇸 South Sudan +211',
    '🇪🇸 Spain +34',
    '🇱🇰 Sri Lanka +94',
    '🇸🇩 Sudan +249',
    '🇸🇷 Suriname +597',
    '🇸🇯 Svalbard and Jan Mayen +4779',
    '🇸🇪 Sweden +46',
    '🇨🇭 Switzerland +41',
    '🇸🇾 Syria +963',
    '🇸🇹 São Tomé and Príncipe +239',
    '🇹🇯 Tajikistan +992',
    '🇹🇿 Tanzania +255',
    '🇹🇭 Thailand +66',
    '🇹🇱 Timor-Leste +670',
    '🇹🇬 Togo +228',
    '🇹🇰 Tokelau +690',
    '🇹🇴 Tonga +676',
    '🇹🇹 Trinidad and Tobago +1868',
    '🇹🇳 Tunisia +216',
    '🇹🇲 Turkmenistan +993',
    '🇹🇨 Turks and Caicos Islands +1649',
    '🇹🇻 Tuvalu +688',
    '🇹🇷 Türkiye +90',
    '🇺🇬 Uganda +256',
    '🇺🇦 Ukraine +380',
    '🇦🇪 United Arab Emirates +971',
    '🇬🇧 United Kingdom +44',
    '🇺🇸 United States +1',
    '🇻🇮 United States Virgin Islands +1340',
    '🇺🇾 Uruguay +598',
    '🇺🇿 Uzbekistan +998',
    '🇻🇺 Vanuatu +678',
    '🇻🇦 Vatican City +39',
    '🇻🇪 Venezuela +58',
    '🇻🇳 Vietnam +84',
    '🇼🇫 Wallis and Futuna +681',
    '🇪🇭 Western Sahara +212',
    '🇾🇪 Yemen +967',
    '🇿🇲 Zambia +260',
    '🇿🇼 Zimbabwe +263',
    '🇦🇽 Åland Islands +358'
  ].join('\n');

  function read(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Private browsing or blocked storage: prefill is a convenience, not a
      // requirement — the customer can still type the code.
    }
  }

  function forget(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Nothing to do: the flag only guards against a reload loop.
    }
  }

  // The referral code has to outlive the tab. Someone scans a poster, gets
  // distracted, and finishes signing up that evening — session storage would
  // have thrown the code away the moment they closed the tab.
  var box;

  function storage() {
    if (box !== undefined) return box;

    try {
      window.localStorage.setItem(SAVED_AT_KEY + '_probe', '1');
      window.localStorage.removeItem(SAVED_AT_KEY + '_probe');
      box = window.localStorage;
    } catch (error) {
      try {
        box = window.sessionStorage;
      } catch (inner) {
        box = null;
      }
    }

    return box;
  }

  function readCode(key) {
    var store = storage();
    if (!store) return null;

    try {
      // A code kept forever would quietly attach a long-dead campaign to an
      // account months later, so it is dropped once the campaign window has
      // realistically passed.
      var savedAt = parseInt(store.getItem(SAVED_AT_KEY), 10);

      if (savedAt && Date.now() - savedAt > CODE_TTL_DAYS * 86400000) {
        [CODE_KEY, FROM_LINK_KEY, SAVED_AT_KEY].forEach(function (stale) {
          store.removeItem(stale);
        });
        return null;
      }

      return store.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeCode(key, value) {
    var store = storage();
    if (!store) return;

    try {
      store.setItem(key, value);
      store.setItem(SAVED_AT_KEY, String(Date.now()));
    } catch (error) {
      // As above: the customer can still type the code.
    }
  }

  function capture() {
    var value = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (!value) return;

    value = value.trim().toLowerCase().slice(0, MAX_CODE_LENGTH);
    if (!value) return;

    writeCode(CODE_KEY, value);
    writeCode(FROM_LINK_KEY, '1');
  }

  function flagRegistration() {
    // Shopify decides where a new account lands — here, the home page. Leave a
    // mark the layout can act on, rather than forcing a redirect with a
    // return_to field, which would fire on failed sign-ups too and swallow the
    // error the customer needs to read.
    var marker = document.querySelector('input[name="form_type"][value="create_customer"]');
    if (!marker || !marker.form) return;

    marker.form.addEventListener('submit', function () {
      write(REGISTERED_KEY, '1');
    });
  }

  function greetNewCustomer() {
    var flag = read(HIGHLIGHT_KEY);
    if (flag !== '1') return;

    try {
      window.sessionStorage.removeItem(HIGHLIGHT_KEY);
    } catch (error) {
      // Nothing to clean up.
    }

    var anchor = document.getElementById('complete-profile');
    if (!anchor) return;

    // Arriving from the redirect, so start at the top — twice, because the
    // browser restores its own scroll position after this script has run.
    function toTop() {
      window.scrollTo(0, 0);
    }

    toTop();
    window.addEventListener('load', toTop, { once: true });

    anchor.classList.add('is-new');
  }

  function fire(field) {
    // The embed keeps its own internal state; without these events it submits
    // the field as empty even though the input shows a value.
    ['input', 'change'].forEach(function (type) {
      field.dispatchEvent(new Event(type, { bubbles: true, composed: true }));
    });
  }

  function setValue(field, value) {
    field.value = value;
    fire(field);
  }

  function lock(field) {
    field.readOnly = true;
    field.setAttribute('aria-readonly', 'true');

    // The embed renders in a shadow root, so no stylesheet of ours can reach
    // it. Inline styles are the only way to make "you can't edit this" visible
    // rather than something the customer discovers by trying.
    field.style.opacity = '0.6';
    field.style.cursor = 'not-allowed';
  }

  // ---------------------------------------------------------------------------
  // Prefill from the customer record
  // ---------------------------------------------------------------------------

  function parsePrefill() {
    var source = document.querySelector('[data-profile-prefill]');
    if (!source) return {};

    try {
      return JSON.parse(source.textContent) || {};
    } catch (error) {
      // Nothing to prefill is a worse form, not a broken one.
      return {};
    }
  }

  function controlsFor(root, id) {
    // A text field carries the field ID on the input itself; a radio group
    // carries it on a wrapper and shares it as the name of every option.
    var named = root.querySelectorAll('[name="' + id + '"]');
    if (named.length) return Array.prototype.slice.call(named);

    var host = root.querySelector('[id="' + id + '"]');
    if (!host) return [];
    if (/^(input|select|textarea)$/i.test(host.tagName)) return [host];

    return Array.prototype.slice.call(host.querySelectorAll('input, select, textarea'));
  }

  function optionFor(select, value) {
    var wanted = value.toLowerCase();

    for (var i = 0; i < select.options.length; i++) {
      var option = select.options[i];
      if (option.value.toLowerCase() === wanted) return option.value;
      if (option.textContent.trim().toLowerCase() === wanted) return option.value;
    }

    return null;
  }

  function tick(boxes, value) {
    var wanted = value.toLowerCase();

    boxes.forEach(function (box) {
      if (box.value.toLowerCase() !== wanted) return;
      box.checked = true;
      fire(box);
    });
  }

  function chooseOption(root, id, value) {
    var shown = root.querySelector('[id="' + id + '-selector-value"]');
    if (!shown) return;

    // Something is picked already — the customer's own answer, or ours from a
    // pass before the embed re-rendered.
    if (shown.textContent.trim()) return;

    // The toggle only advertises aria-controls while its list is open, so reach
    // it through the span showing the chosen answer, which is always there.
    var toggle = shown.closest('button');
    if (!toggle) return;

    if (toggle.getAttribute('aria-expanded') !== 'true') toggle.click();

    var tries = 0;

    (function waitForList() {
      var list = root.querySelector('[id="' + id + '-selector-dropdown"]');

      // Opening the dropdown is the component's own render, so the list turns
      // up a tick or two after the click.
      if (!list) {
        if (++tries > 20) return;
        window.setTimeout(waitForList, 25);
        return;
      }

      var options = list.querySelectorAll('[role="option"]');
      var wanted = value.toLowerCase();

      // Labels are translated, ids are not: a record saying "Male" has to find
      // 男性 on the Chinese storefront, and select-custom#gender-Male is the
      // only thing on screen still saying so. Clicking is the only way in —
      // the answer lives in the component's own state, so nothing set on the
      // markup would survive submission.
      var stored = ('select-' + id + '-' + value).toLowerCase();

      for (var i = 0; i < options.length; i++) {
        if (options[i].id.toLowerCase() !== stored) continue;
        options[i].click();
        return;
      }

      for (var j = 0; j < options.length; j++) {
        if (options[j].textContent.trim().toLowerCase() !== wanted) continue;
        options[j].click();
        return;
      }

      // No match — close it again rather than leaving a list hanging open over
      // a field the customer has not touched.
      toggle.click();
    })();
  }

  function applySaved(root, id, value) {
    var controls = controlsFor(root, id);

    // Not every field is an input: the embed builds its dropdowns out of a
    // button and a list that is only in the DOM while it is open.
    if (!controls.length) {
      chooseOption(root, id, value);
      return;
    }

    var boxes = controls.filter(function (control) {
      return control.type === 'radio' || control.type === 'checkbox';
    });

    if (boxes.length) {
      tick(boxes, value);
      return;
    }

    var field = controls[0];

    // Never overwrite what is already on screen: the customer's email and a
    // code that arrived from a QR link are put in elsewhere and win.
    if (field.value) return;

    if (field.tagName.toLowerCase() === 'select') {
      var match = optionFor(field, value);
      // An answer the rebuilt form no longer offers is left unset rather than
      // forced into whichever option happens to sit first.
      if (match !== null) setValue(field, match);
      return;
    }

    setValue(field, value);
  }

  function prefill(root, saved) {
    Object.keys(saved).forEach(function (id) {
      var value = saved[id];
      if (value === null || value === undefined) return;

      value = String(value).trim();
      if (value) applySaved(root, id, value);
    });
  }

  function parseCodes(config) {
    var source = document.querySelector('[data-referral-codes]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent) || [];
    } catch (error) {
      // A malformed list must not take the form down with it — fall back to
      // accepting whatever the customer types.
      return [];
    }
  }

  function today() {
    // Compared against plain YYYY-MM-DD metaobject dates.
    var now = new Date();
    var month = String(now.getMonth() + 1).padStart(2, '0');
    var day = String(now.getDate()).padStart(2, '0');
    return now.getFullYear() + '-' + month + '-' + day;
  }

  // Shopify derives a metaobject field key from whatever label the merchant
  // typed, so accept the spellings the code list is likely to come back with
  // rather than forcing one naming scheme on the marketing team.
  var FIELD_ALIASES = {
    code: ['code', 'referral_code', 'value'],
    active: ['active', 'is_active', 'enabled', 'status'],
    from: ['starts_on', 'start_date', 'starts_at', 'valid_from', 'start'],
    to: ['ends_on', 'end_date', 'ends_at', 'valid_to', 'expires_on', 'end']
  };

  function field(entry, name) {
    var keys = FIELD_ALIASES[name];

    for (var i = 0; i < keys.length; i++) {
      var value = entry[keys[i]];
      if (value !== undefined && value !== null && value !== '') return value;
    }

    return null;
  }

  function isValid(code, codes, hasCodeList) {
    // A store with no code list configured accepts anything. Once a list is
    // configured an empty one means exactly that — nothing is valid — otherwise
    // drafting every code would quietly turn validation off instead of on.
    if (!hasCodeList) return true;

    var now = today();

    return codes.some(function (entry) {
      if (!entry || String(field(entry, 'code') || '').toLowerCase() !== code) return false;

      // Where the list carries an active flag, only an explicit yes counts:
      // unticking the box stores nothing at all, not false. A list without the
      // field is treated as all-active.
      var active = field(entry, 'active');
      if (entry.has_active_field && active !== true && active !== 'true') return false;
      if (active === false || active === 'false') return false;

      var from = field(entry, 'from');
      if (from && now < String(from).slice(0, 10)) return false;

      var to = field(entry, 'to');
      if (to && now > String(to).slice(0, 10)) return false;

      return true;
    });
  }

  function noticeFor(field, message) {
    var root = field.getRootNode();
    var notice = root.querySelector('[' + NOTICE_ATTR + ']');

    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute(NOTICE_ATTR, '');
      notice.setAttribute('role', 'status');
      notice.style.margin = '6px 0 0';
      notice.style.fontSize = '0.875em';
      notice.style.lineHeight = '1.4';
      notice.style.color = '#8a6116';
      notice.textContent = message;
      (field.parentNode || field).appendChild(notice);
    }

    return notice;
  }

  function watchValidity(field, config, codes, accepted) {
    var message = config.dataset.invalidMessage || '';
    var hasCodeList = config.dataset.hasCodeList === 'true';
    var known = String(accepted || '').trim().toLowerCase();

    function ok(code) {
      // A code already sitting on the customer record stays good even once it
      // has expired. It was earned, and someone reopening this step to fix
      // their phone number must not have it flagged and dropped underneath them.
      return !code || code === known || isValid(code, codes, hasCodeList);
    }

    function check() {
      var code = field.value.trim().toLowerCase();

      // Codes are matched exactly by the Flow workflow that turns them into
      // customer tags, and Flow compares case-sensitively.
      if (code !== field.value) setValue(field, code);

      var notice = noticeFor(field, message);
      notice.style.display = ok(code) ? 'none' : 'block';
    }

    field.addEventListener('input', check);
    field.addEventListener('blur', check);
    check();

    return function isFieldValid() {
      return ok(field.value.trim().toLowerCase());
    };
  }

  function dropInvalidOnSubmit(root, field, isFieldValid) {
    // The customer was told they can "continue signing up without a code", so
    // an invalid one is cleared rather than blocking the submission — nothing
    // unrecognised is stored, and Flow can tag on any code that gets through.
    root.addEventListener(
      'submit',
      function () {
        if (!isFieldValid()) setValue(field, '');
      },
      true
    );
  }

  // ---------------------------------------------------------------------------
  // Phone country code
  // ---------------------------------------------------------------------------

  function regionOf(label) {
    // A flag emoji is its ISO country code spelled in regional indicators, so
    // the list needs no second column to say which country a line is.
    var flag = flagOf(label);
    if (!flag) return '';

    return String.fromCharCode(
      label.codePointAt(0) - 0x1f1e6 + 65,
      label.codePointAt(2) - 0x1f1e6 + 65
    );
  }

  function localName(region) {
    if (!region) return '';

    if (!namers) {
      try {
        namers = {
          long: new Intl.DisplayNames([locale], { type: 'region' }),
          short: new Intl.DisplayNames([locale], { type: 'region', style: 'short' })
        };
      } catch (error) {
        // An old browser keeps the English names the list ships with.
        namers = {};
      }
    }

    var namer = SHORT_NAME[region] ? namers.short : namers.long;
    if (!namer) return '';

    try {
      var name = namer.of(region);
      // An unknown region comes back as the code itself.
      return name === region ? '' : name;
    } catch (error) {
      return '';
    }
  }

  function localise(codes) {
    // The browser already knows every country's name in the language the page
    // is being read in — far better than shipping a translation of the list per
    // language and keeping them all straight.
    codes.forEach(function (entry) {
      var flag = flagOf(entry.label);
      var plain = flag ? entry.label.slice(flag.length).trim() : entry.label;
      var name = localName(regionOf(entry.label)) || plain;

      entry.name = name;
      entry.label = flag ? flag + ' ' + name : name;
    });

    // Sorted the way the customer reads it, with the store's own markets left
    // on top where they were.
    var tail = codes.splice(PINNED_COUNTRIES);

    tail.sort(function (a, b) {
      return a.name.localeCompare(b.name, locale);
    });

    codes.push.apply(codes, tail);
  }

  function parseCountryCodes(config) {
    // One country per line, label first and dial code last: "Hong Kong +852".
    // Marketing edits this list in the theme editor, so read it loosely rather
    // than insisting on a format nobody will remember.
    // Left blank the built-in international list stands in; "none" drops the
    // dropdown altogether.
    var configured = (config.dataset.countryCodes || '').trim();
    if (configured.toLowerCase() === 'none') return [];

    locale = config.dataset.locale || document.documentElement.lang || locale;

    var codes = [];

    (configured || DEFAULT_COUNTRY_CODES).split('\n').forEach(function (line) {
      var match = line.match(/\+\s*([\d\s-]{1,8})/);
      if (!match) return;

      var dial = '+' + match[1].replace(/\D/g, '');
      if (dial === '+') return;

      var label = line.slice(0, match.index).replace(/[\s|,()\-]+$/, '').trim();
      codes.push({ label: label || dial, dial: dial });
    });

    // Only the built-in list: a list marketing typed out is left in their own
    // words and their own order.
    if (!configured) localise(codes);

    return codes;
  }

  function findPhoneField(root, id) {
    if (id) {
      var configured = root.querySelector('[id="' + id + '"]');
      if (configured) return configured;
    }

    // Field IDs change whenever the form is rebuilt in the Forms app. Rather
    // than silently dropping the country code, fall back to whatever looks like
    // a phone input.
    var tel = root.querySelector('input[type="tel"]');
    if (tel) return tel;

    var inputs = root.querySelectorAll('input');

    for (var i = 0; i < inputs.length; i++) {
      var hints = [inputs[i].id, inputs[i].name, inputs[i].placeholder, inputs[i].autocomplete]
        .join(' ')
        .toLowerCase();

      if (hints.indexOf('phone') > -1 || hints.indexOf('tel') > -1) return inputs[i];
    }

    return null;
  }

  function fieldContainer(field, root) {
    // The embed floats each label over its own input, positioned against a
    // wrapper element. Put the dropdown beside that wrapper rather than beside
    // the bare input, or the label ends up floating over the dropdown instead.
    var node = field;
    var depth = 0;

    while (node.parentNode && node.parentNode !== root && depth < 5) {
      var parent = node.parentNode;

      // Never climb past the form itself — the dropdown belongs beside one row,
      // not around every field in the form.
      if (parent.tagName === 'FORM' || parent.tagName === 'FIELDSET') break;
      if (parent.querySelectorAll('input, select, textarea').length > 1) break;

      node = parent;
      depth++;
    }

    return node;
  }

  // Shorthand properties come back empty from getComputedStyle in some
  // browsers, so copy the longhands the dropdown needs to pass for part of the
  // form rather than a browser default control dropped next to it.
  var COPIED_STYLES = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'letterSpacing',
    'color',
    'backgroundColor',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius'
  ];

  ['Top', 'Right', 'Bottom', 'Left'].forEach(function (side) {
    ['Width', 'Style', 'Color'].forEach(function (property) {
      COPIED_STYLES.push('border' + side + property);
    });
  });

  function matchInputStyle(select, field) {
    var computed = window.getComputedStyle(field);

    COPIED_STYLES.forEach(function (property) {
      var value = computed[property];
      if (value) select.style[property] = value;
    });

    // Match the outer box rather than the inner one: a select pads its own text
    // differently from an input, so equal heights and equal padding still come
    // out looking clipped.
    select.style.boxSizing = 'border-box';
    if (field.offsetHeight) select.style.height = field.offsetHeight + 'px';

    select.style.paddingLeft = '10px';
    select.style.paddingRight = '24px'; // room for the native dropdown arrow
    select.style.flex = '0 0 auto';
    select.style.width = 'auto';
    select.style.maxWidth = '45%';
  }

  function injectPhoneStyles(root) {
    if (root.querySelector('[' + PHONE_STYLE_ATTR + ']')) return;

    // No stylesheet of ours reaches inside the shadow root, so the layout has to
    // be planted in there with the fields it lays out.
    var style = document.createElement('style');
    style.setAttribute(PHONE_STYLE_ATTR, '');
    style.textContent =
      '.' + PHONE_CLASS + '{display:flex;gap:8px;align-items:center;width:100%}' +
      '.' + PHONE_CLASS + ' > :last-child{flex:1 1 auto;min-width:0;margin:0}';
    root.appendChild(style);
  }

  function splitExistingValue(select, field, codes) {
    var value = field.value.trim();
    if (value.charAt(0) !== '+') return;

    // Longest first, so +852 is not claimed by a +85 sitting above it.
    var sorted = codes.slice().sort(function (a, b) {
      return b.dial.length - a.dial.length;
    });

    for (var i = 0; i < sorted.length; i++) {
      if (value.indexOf(sorted[i].dial) !== 0) continue;

      select.value = sorted[i].dial;
      setValue(field, value.slice(sorted[i].dial.length).trim());
      return;
    }
  }

  function flagOf(label) {
    // Regional indicator pairs live at U+1F1E6–U+1F1FF and take two surrogate
    // pairs each. A list without flags — marketing may well type plain names —
    // simply has none to show.
    var first = label.codePointAt(0);
    if (first >= 0x1f1e6 && first <= 0x1f1ff) return label.slice(0, 4);
    return '';
  }

  function fill(select, key) {
    Array.prototype.forEach.call(select.options, function (option) {
      option.textContent = option.dataset[key] || option.dataset.full;
    });
  }

  function fitWidth(select) {
    // Sized to the text it is actually showing, so the phone field keeps the
    // rest of the row. Measured rather than guessed: the theme's font decides
    // how wide "+852 🇭🇰" comes out.
    var option = select.options[select.selectedIndex];
    if (!option) return;

    var computed = window.getComputedStyle(select);
    var probe = document.createElement('span');

    probe.textContent = option.dataset.short;
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;';
    probe.style.font = computed.font || computed.fontSize + ' ' + computed.fontFamily;
    probe.style.letterSpacing = computed.letterSpacing;

    select.parentNode.appendChild(probe);
    var text = probe.offsetWidth;
    select.parentNode.removeChild(probe);

    // A hidden form measures nothing; leave the width alone rather than
    // collapsing the control to its padding.
    if (!text) return;

    // Padding either side, the arrow, and room to spare: a flag emoji takes up
    // more width inside a select than the same emoji measured in a span, and a
    // clipped flag is the first thing anyone notices.
    select.style.width = text + 56 + 'px';
  }

  function shorten(select) {
    fill(select, 'short');
    fitWidth(select);
  }

  function keepReadable(select) {
    // Closed, the control shows "+852 🇭🇰". Open, every option needs its country
    // name back — so restore them the moment the customer reaches for it, and
    // trim again once they have chosen.
    // Not on focus: tabbing through the form should not make the row jump about
    // when the customer has not even opened the list.
    ['mousedown', 'touchstart', 'keydown'].forEach(function (type) {
      select.addEventListener(type, function () {
        fill(select, 'full');
      });
    });

    ['change', 'blur'].forEach(function (type) {
      select.addEventListener(type, function () {
        shorten(select);
      });
    });

    shorten(select);
  }

  function addCountryCode(root, config, codes) {
    if (!codes.length) return null;

    var field = findPhoneField(root, config.dataset.phoneField);
    if (!field) return null;
    if (field.closest && field.closest('.' + PHONE_CLASS)) return null;

    injectPhoneStyles(root);

    var select = document.createElement('select');
    select.className = PHONE_CLASS + '__code';
    select.setAttribute('aria-label', config.dataset.countryCodeLabel || 'Country code');

    codes.forEach(function (entry) {
      var option = document.createElement('option');
      var flag = flagOf(entry.label);

      option.value = entry.dial;
      // Two spellings of the same thing: the full one to pick from, and a short
      // one for the closed dropdown, which only has room for the part that
      // actually has to be read back — the dial code being applied.
      option.dataset.full = entry.dial + ' ' + entry.label;
      option.dataset.short = flag ? entry.dial + ' ' + flag : entry.dial;
      option.textContent = option.dataset.full;
      select.appendChild(option);
    });

    matchInputStyle(select, field);

    var container = fieldContainer(field, root);
    var wrapper = document.createElement('div');
    wrapper.className = PHONE_CLASS;

    // The container carries the row's own spacing; the wrapper takes its place
    // in the flow, so the form keeps its rhythm.
    wrapper.style.marginBottom = window.getComputedStyle(container).marginBottom;

    container.parentNode.insertBefore(wrapper, container);
    wrapper.appendChild(select);
    wrapper.appendChild(container);

    splitExistingValue(select, field, codes);
    keepReadable(select);

    return { select: select, field: field };
  }

  function composeNumber(select, field) {
    var local = field.value.trim();

    // The field is optional: nothing typed stays nothing.
    if (!local) return;

    // A number typed in full already carries its own country code.
    if (local.charAt(0) === '+') return;

    // The trunk prefix is dropped once a country code is in front: 0912 becomes
    // +886 912.
    local = local.replace(/^0+/, '');
    if (!local) return;

    setValue(field, select.value + CODE_SEPARATOR + local);
  }

  function composeOnSubmit(root, select, field) {
    function compose() {
      composeNumber(select, field);
    }

    // Which event carries the submission depends on how the embed is built, so
    // cover the three that can. Composing twice is harmless — a number already
    // starting with "+" is left alone.
    root.addEventListener('submit', compose, true);

    root.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (target && target.closest && target.closest('button, [type="submit"]')) compose();
      },
      true
    );

    field.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') compose();
    });
  }

  function setupPhone(root, config, codes) {
    var parts = addCountryCode(root, config, codes);
    if (parts) composeOnSubmit(root, parts.select, parts.field);
  }

  function keepPhoneAlive(root, config, codes) {
    if (!codes.length || !window.MutationObserver) return;

    // The embed re-renders on validation errors and throws our dropdown out with
    // its own markup. Put it back.
    var observer = new MutationObserver(function () {
      if (!root.querySelector('.' + PHONE_CLASS)) {
        setupPhone(root, config, codes);
        return;
      }

      // A re-render can also hand the field back the whole number we had
      // already split — a locale's translations arriving late do exactly that.
      // Splitting again is free: a number with no dial code in front of it is
      // left alone.
      var select = root.querySelector('.' + PHONE_CLASS + '__code');
      var field = findPhoneField(root, config.dataset.phoneField);
      if (select && field) splitExistingValue(select, field, codes);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Submission reminder, success state, and the way back
  // ---------------------------------------------------------------------------

  function remind(root, config, field) {
    // A code that arrived by link is filled in for the customer, so nothing on
    // screen says it still has to be submitted. Say it, because walking away
    // from this step is exactly how a referral goes missing.
    var message = config.dataset.pendingMessage;
    if (!message) return;
    if (root.querySelector('[' + REMINDER_ATTR + ']')) return;

    var notice = document.createElement('p');
    notice.setAttribute(REMINDER_ATTR, '');
    notice.setAttribute('role', 'status');
    notice.style.margin = '6px 0 0';
    notice.style.fontSize = '0.875em';
    notice.style.lineHeight = '1.4';
    notice.style.opacity = '0.75';
    notice.textContent = message;
    (field.parentNode || field).appendChild(notice);
  }

  function watchSuccess(root, config) {
    var header = config.querySelector('.page-header');
    var again = config.querySelector('[data-profile-again]');

    if (!header && !again) return;
    if (!window.MutationObserver) return;

    function apply() {
      // The embed swaps the whole form out for its success message, so the
      // form's absence is the signal — there is no class name of Shopify's
      // stable enough to hang this on.
      var done = !root.querySelector('form');

      // Once it is done, the heading and the "all fields are optional" note are
      // answering a question nobody is asking any more.
      if (header) header.style.display = done ? 'none' : '';
      if (again) again.hidden = !done;
    }

    new MutationObserver(apply).observe(root, { childList: true, subtree: true });
    apply();
  }

  function clearDismissal() {
    // Shopify Forms marks a form as done for this browser the moment it is
    // submitted — <form id>-dismissed-after-submit — and from then on renders
    // nothing at all, which is precisely what the link below is for. The id is
    // on the app block's own root, so there is nothing to keep in a setting.
    var host = document.querySelector('[data-forms-id]');
    var id = host && (String(host.dataset.formsId).match(/\d+/) || [])[0];
    if (!id) return false;

    try {
      window.localStorage.removeItem(id + DISMISSED_SUFFIX);
      return true;
    } catch (error) {
      return false;
    }
  }

  function recoverEmbed() {
    if (new URLSearchParams(window.location.search).get(EDIT_PARAM) === null) return;

    window.setTimeout(function () {
      var host = document.querySelector('shopify-forms-embed');

      if (host && host.shadowRoot && host.shadowRoot.querySelector('input')) {
        // It came. Let the next round of edits recover for itself.
        forget(RETRY_KEY);
        return;
      }

      // It did not, which means the app read its dismissal mark before the
      // section could clear it — a race the inline script wins on a page
      // served fresh and loses on one served from cache. Clear it and come
      // back, once: a reload loop is worse than a form that stays away.
      if (read(RETRY_KEY)) return;
      if (!clearDismissal()) return;

      write(RETRY_KEY, '1');
      window.location.reload();
    }, RECOVER_AFTER);
  }

  function keepReopenLinkAlive(link) {
    link.addEventListener('click', function (event) {
      // Clear the mark on the way out. The section clears it inline on the way
      // in as well, but that is a race against the app's own loader — and this
      // side of the navigation has no race to lose.
      clearDismissal();

      // Second time round the link points at the page the customer is already
      // standing on. The browser reads that as a jump to the fragment, never
      // reloads, and the embed keeps showing its thank-you message — so the
      // link looks dead from the second use onwards. Reload it by hand.
      if (link.pathname !== window.location.pathname) return;
      if (link.search !== window.location.search) return;

      event.preventDefault();
      window.location.reload();
    });
  }

  // ---------------------------------------------------------------------------
  // Policy links
  // ---------------------------------------------------------------------------

  function policyScope(root, phrases) {
    // The line the app prints under the button is the smallest element carrying
    // every phrase at once — narrow enough not to catch a consent tickbox that
    // happens to mention one of them.
    var found = null;

    Array.prototype.forEach.call(
      root.querySelectorAll('p, span, div, small, footer'),
      function (element) {
        var text = element.textContent.toLowerCase();

        var carries = phrases.every(function (phrase) {
          return text.indexOf(phrase) > -1;
        });

        if (!carries) return;
        if (!found || found.contains(element)) found = element;
      }
    );

    return found;
  }

  function applyNotice(scope, notice) {
    // The sentence itself comes from the theme's locale file, not from the
    // customer, so it goes in whole — the app writes its own wording here and
    // offers no way to change it.
    scope.setAttribute(POLICY_ATTR, '');
    scope.innerHTML = notice;

    Array.prototype.forEach.call(scope.querySelectorAll('a'), function (anchor) {
      // The block is configured with link and text in the same colour, so
      // underline them — otherwise nothing on screen says they can be clicked.
      anchor.style.textDecoration = 'underline';
    });
  }

  function linkPolicies(root, config) {
    var notice = config.dataset.policyNotice;
    if (!notice) return;

    // The app's own sentence names both policies, which is how the line is
    // found. Our replacement names them too, so the marker is what stops the
    // two from chasing each other round the observer below.
    var phrases = [config.dataset.privacyPhrase, config.dataset.termsPhrase]
      .filter(Boolean)
      .map(function (phrase) {
        return phrase.toLowerCase();
      });

    if (phrases.length < 2) return;

    var scope = policyScope(root, phrases);
    if (!scope || scope.hasAttribute(POLICY_ATTR)) return;

    applyNotice(scope, notice);
  }

  function keepPolicyLinks(root, config) {
    linkPolicies(root, config);
    if (!window.MutationObserver) return;

    // The embed re-renders on validation errors and again on success, putting
    // its own wording back each time. Running again is harmless: a line already
    // carrying our marker is left alone.
    new MutationObserver(function () {
      linkPolicies(root, config);
    }).observe(root, { childList: true, subtree: true });
  }

  function reopen() {
    // A customer who has submitted never sees this step again — the section
    // arrives hidden. The link under the success message carries the parameter
    // that brings it back, so a referral code they skipped can still be added.
    var section = document.getElementById('complete-profile');
    if (!section || section.dataset.completed !== 'true') return;
    if (new URLSearchParams(window.location.search).get(EDIT_PARAM) === null) return;

    section.style.display = '';
    section.removeAttribute('data-completed');

    // The browser has already given up on the #complete-profile fragment by
    // now — it pointed at something with no box.
    window.setTimeout(function () {
      section.scrollIntoView({ block: 'start' });
    }, 0);
  }

  function whenFormReady(callback) {
    var tries = 0;

    (function poll() {
      var host = document.querySelector('shopify-forms-embed');
      var root = host && host.shadowRoot;

      if (root && root.querySelector('input')) {
        callback(root);
        return;
      }

      if (++tries > POLL_LIMIT) return;
      window.setTimeout(poll, POLL_INTERVAL);
    })();
  }

  function enhance(config) {
    var codes = parseCodes(config);

    whenFormReady(function (root) {
      var email = config.dataset.customerEmail;
      var emailField = root.querySelector('[id="' + config.dataset.emailField + '"]');

      // Pin the email to the signed-in account. Left free, a typo would push the
      // answers onto a different customer — or create a brand new one.
      if (email && emailField) {
        setValue(emailField, email);
        lock(emailField);
      }

      watchSuccess(root, config);
      keepPolicyLinks(root, config);

      // Ahead of the phone dropdown, so a number saved as "+852 61234567" is
      // split back into its dial code and the rest rather than arriving whole
      // in a field that is about to grow a country selector.
      var saved = parsePrefill();
      prefill(root, saved);

      var countryCodes = parseCountryCodes(config);
      setupPhone(root, config, countryCodes);
      keepPhoneAlive(root, config, countryCodes);

      var referralField = root.querySelector('[id="' + config.dataset.referralField + '"]');
      if (!referralField) return;

      var storedCode = readCode(CODE_KEY);
      var fromLink = readCode(FROM_LINK_KEY) === '1';

      if (storedCode) setValue(referralField, storedCode);
      if (storedCode && fromLink) remind(root, config, referralField);

      var isFieldValid = watchValidity(referralField, config, codes, saved[config.dataset.referralField]);
      dropInvalidOnSubmit(root, referralField, isFieldValid);

      // Only lock a code that arrived from a QR code and actually checks out —
      // otherwise the customer is stuck with a code they cannot correct.
      if (storedCode && fromLink && config.dataset.lockReferral === 'true' && isFieldValid()) {
        lock(referralField);
      }
    });
  }

  capture();
  flagRegistration();
  greetNewCustomer();
  reopen();
  recoverEmbed();

  var config = document.querySelector('[data-profile-completion]');

  // The link under the thank-you message, and the button on the account page
  // that stands in for it on every later visit.
  Array.prototype.forEach.call(
    document.querySelectorAll('[data-profile-again] a[href], a[data-profile-edit]'),
    keepReopenLinkAlive
  );

  if (config) enhance(config);
})();
